import { Button, Col, Form, Input, Modal, Row, Select, Space, Table } from "antd";
import { useEffect, useState } from "react";
import { VALIDATION_MESSAGE_CONFIG } from "../../../../../constants/constants";
import { AssetClassModel, BaselClassificationModel } from "../../../../../models/scenario-frontier.model";
import { ScenarioFrontierService } from "../../../../../services/scenario-frontier.service";
import { AMOUNT_UTIL } from "../../../../../utils/amount.utils";
import i18n from "../../../../../utils/i18n";
import { AddIcon, DoubleArrowIcon } from "../../../../atoms/icon";
import PercentInput from "../../../../atoms/percent-input";
import BaselClassifications from "./basel-classifications";
import "./index.scss";

const AssetClassesConfiguration = () => {

    const [data, setData] = useState<AssetClassModel[]>([]);
    const [dataLoading, setDataLoading] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [form] = Form.useForm();
    const [editItem, setEditItem] = useState<AssetClassModel>();
    const [baselClassificationSelect, setBaselClassificationSelect] = useState<BaselClassificationModel[]>([]);
    const [baselClassificationData, setBaselClassificationData] = useState<BaselClassificationModel[]>([]);
    const [baselClassificationLoading, setbaselClassificationLoading] = useState(false);

    const columns: any[] = [
        {
            title: i18n.t("ScenarioFrontier.lbSegment"),
            dataIndex: 'segment',
            key: 'segment'
        },
        {
            title: i18n.t("ScenarioFrontier.lbLGD"),
            dataIndex: 'lgd',
            key: 'lgd',
            align: "right",
            render: (mean: number) => AMOUNT_UTIL.converNumberToPercentage(mean)
        },
        {
            title: i18n.t("ScenarioFrontier.lbVolatility"),
            dataIndex: 'volatility',
            key: 'volatility',
            align: "right",
            render: (volatility: number) => AMOUNT_UTIL.converNumberToPercentage(volatility)
        }, {
            title: i18n.t("ScenarioFrontier.lbCorrelation"),
            dataIndex: 'correlation',
            key: 'correlation',
            align: "right",
            render: (correlation: number) => AMOUNT_UTIL.converNumberToPercentage(correlation)
        }, {
            title: <span>{i18n.t("ScenarioFrontier.lbBaselClassificationMapping")}</span>,
            dataIndex: 'basel_classification_id',
            key: 'basel_classification_id',
            // className: "base-classification",
            render: (id: number) => <span>
                {baselClassificationSelect.find(x => x.id === id)?.name}
                {/* <OpenNewIcon /> */}
            </span>
        },
        {
            title: i18n.t("ScenarioFrontier.lbActions"),
            dataIndex: 'actions',
            key: 'actions',
            render: (text: string, record: AssetClassModel) => <>
                <Space>
                    <span className="blue-text action-text" onClick={() => edit(record.id)}>
                        {i18n.t("ScenarioFrontier.lbEdit")}
                    </span>
                    <span className="red-text action-text" onClick={() => deleteItem(record.id)}>
                        {i18n.t("ScenarioFrontier.lbDelete")}
                    </span>
                </Space>
            </>
        }
    ]


    const getData = () => {
        setDataLoading(true);
        ScenarioFrontierService.getAssetClasses().then(res => {
            setData(res.data);
        }).finally(() => setDataLoading(false));
    }

    const getBaselClassificationData = () => {
        setbaselClassificationLoading(true);
        ScenarioFrontierService.getBaselClassifications().then(res => {
            setBaselClassificationData(res.data);
            setBaselClassificationSelect(JSON.parse(JSON.stringify(res.data)));
        }).finally(() => setbaselClassificationLoading(false));
    }

    const updateBaselClassification = (item: BaselClassificationModel) => {
        ScenarioFrontierService.updateBaselClassification(item).then(res => {
            getBaselClassificationData();
        });
    }

    const edit = (id: number) => {
        const item = data.find(p => p.id === id);
        setEditItem(item);
        form.setFieldsValue(item);
        setShowPopup(true);
    }

    const addNew = () => {
        form.resetFields();
        setEditItem(undefined);
        setShowPopup(true);
    }

    const save = () => {
        form.submit();
    }

    const formSubmit = async () => {
        const item = form.getFieldsValue();
        if (editItem && editItem.id) {
            await ScenarioFrontierService.updateAssetClasses(item);
        } else {
            await ScenarioFrontierService.addAssetClasses(item);
        }

        getData();
        setShowPopup(false);
    }

    const deleteItem = (id: number) => {
        Modal.confirm({
            content: i18n.t("ScenarioFrontier.contentDelete"),
            onCancel: () => { submitDelete(id) },
            icon: '',
            width: 304,
            okText: i18n.t("Global.lbCancel"),
            cancelText: i18n.t("ScenarioFrontier.btnDelete")
        });
    }

    const submitDelete = async (id: number) => {
        await ScenarioFrontierService.deleteAssetClasses(id);
        getData();
    }

    useEffect(() => {
        getData();
        getBaselClassificationData();
    }, [])

    return (
        <div className="asset-class-configuration">
            <h4>
                {i18n.t("ScenarioFrontier.lbAssetClasses")}
            </h4>

            <p className="sub-title">
                <DoubleArrowIcon />
                {i18n.t("ScenarioFrontier.lbAssetClass")}
            </p>

            <Row>
                <Col span={24}>
                    <Table
                        className="second-table"
                        bordered
                        pagination={false}
                        loading={dataLoading}
                        dataSource={data}
                        columns={columns} rowKey="id" />
                </Col>
            </Row>

            <Row>
                <Col span={24}>
                    <Button className="add-more" type="default" shape="round" onClick={addNew}>
                        <AddIcon className="svg-icon" /> {i18n.t("ScenarioFrontier.btnAddmore")}
                    </Button>
                </Col>
            </Row>

            <BaselClassifications data={baselClassificationData}
                loading={baselClassificationLoading}
                update={updateBaselClassification} />

            <Modal
                width={600}
                className="frontier-add-modal"
                title={editItem && editItem.id ? i18n.t("ScenarioFrontier.ttEditAssetClass") : i18n.t("ScenarioFrontier.ttNewAsset")}
                visible={showPopup}
                onCancel={() => { setShowPopup(false) }}
                footer={<Button htmlType="submit" type="primary" shape="round" onClick={save}>
                    {editItem && editItem.id ? i18n.t("ScenarioFrontier.btnSave") : i18n.t("ScenarioFrontier.btnSaveAdd")}
                </Button>}
            >
                <Form
                    layout="vertical"
                    className="modal-form"
                    onFinish={formSubmit}
                    form={form}
                    validateMessages={VALIDATION_MESSAGE_CONFIG}>
                    <Form.Item
                        name="id"
                        hidden>
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="segment"
                        label={i18n.t("ScenarioFrontier.lbName")}
                        rules={[{ required: true }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="lgd"
                        label={i18n.t("ScenarioFrontier.lbLGD")}
                        rules={[{ required: true }]}
                    >
                        <PercentInput />
                    </Form.Item>

                    <Form.Item
                        name="volatility"
                        label={i18n.t("ScenarioFrontier.lbVolatility")}
                        rules={[{ required: true }]}
                    >
                        <PercentInput />
                    </Form.Item>

                    <Form.Item
                        name="correlation"
                        label={i18n.t("ScenarioFrontier.lbCorrelation")}
                        rules={[{ required: true }]}
                    >
                        <PercentInput />
                    </Form.Item>

                    <Form.Item
                        name="basel_classification_id"
                        label={i18n.t("ScenarioFrontier.lbBaselClassificationMapping")}
                        rules={[{ required: true }]}
                    >
                        <Select>
                            {
                                baselClassificationSelect.map(x =>
                                    <Select.Option key={x.id} value={x.id}>{x.name}</Select.Option>
                                )
                            }
                        </Select>
                    </Form.Item>

                </Form>
            </Modal>

        </div>
    )
}

export default AssetClassesConfiguration;